import { useMemo } from "react";
import { useHistory } from "react-router-dom";

import { VARIANTS_QUERY_PARAM } from "../consts";
import {
  buildGroupQueryKey,
  buildGroupSelectionToken,
  setParamValues,
} from "../helpers";
import { VariantGroup, VariantOption } from "../types";

import { AccordionSection } from "./AccordionSection";

type VariantSection =
  | { type: "group"; group: VariantGroup }
  | { type: "accordion"; label: string; groups: VariantGroup[] };

type VariantBlock =
  | { type: "plain"; variants: VariantOption[] }
  | { type: "section"; label: string; variants: VariantOption[] };

const buildSections = (variantGroups: VariantGroup[]): VariantSection[] => {
  const sections: VariantSection[] = [];

  for (const group of variantGroups) {
    const accordionLabel = group.accordion;
    if (!accordionLabel) {
      sections.push({ type: "group", group });
      continue;
    }

    const last = sections[sections.length - 1];
    if (last?.type === "accordion" && last.label === accordionLabel) {
      last.groups.push(group);
      continue;
    }

    sections.push({
      type: "accordion",
      label: accordionLabel,
      groups: [group],
    });
  }

  return sections;
};

/** Groups consecutive variants that share a `section` label into accordion blocks. */
const buildVariantBlocks = (variants: VariantOption[]): VariantBlock[] => {
  const blocks: VariantBlock[] = [];

  for (const variant of variants) {
    if (variant.nestedUnder != null) {
      continue;
    }
    const sectionLabel = variant.section;
    const last = blocks[blocks.length - 1];

    if (!sectionLabel) {
      if (last?.type === "plain") {
        last.variants.push(variant);
      } else {
        blocks.push({ type: "plain", variants: [variant] });
      }
      continue;
    }

    if (last?.type === "section" && last.label === sectionLabel) {
      last.variants.push(variant);
      continue;
    }

    blocks.push({ type: "section", label: sectionLabel, variants: [variant] });
  }

  return blocks;
};

export const VariantsPanel: React.FC<{
  variantGroups: VariantGroup[];
}> = ({ variantGroups }) => {
  const history = useHistory();
  const sections = useMemo(() => buildSections(variantGroups), [variantGroups]);

  const buildVariantTokens = (override?: {
    groupKey: string;
    value: number;
  }): string[] =>
    variantGroups
      .map((group) => {
        const groupKey = buildGroupQueryKey(group);
        const valueForGroup =
          override?.groupKey === groupKey ? override.value : group.active;
        const hasValue = group.variants.some(
          (variant) => variant.variantNumber === valueForGroup
        );
        if (!hasValue) {
          return null;
        }
        return buildGroupSelectionToken(groupKey, String(valueForGroup));
      })
      .filter((token): token is string => token !== null);

  const updateVariants = (override: {
    groupKey: string;
    value: number;
  }): void => {
    const params = new URLSearchParams(location.search);
    setParamValues(params, VARIANTS_QUERY_PARAM, buildVariantTokens(override));
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : undefined,
      hash: location.hash,
    });
  };

  const renderVariantButton = (group: VariantGroup, v: VariantOption) => {
    const nested = group.variants.filter(
      (option) => option.nestedUnder === v.variantNumber
    );
    const isActive =
      v.variantNumber === group.active ||
      nested.some((option) => option.variantNumber === group.active);
    const isExact = v.variantNumber === group.active;

    return (
      <div key={v.variantNumber}>
        <button
          onClick={() => {
            const groupKey = buildGroupQueryKey(group);
            group.onChange(v.variantNumber);
            updateVariants({ groupKey, value: v.variantNumber });
          }}
          className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-1.5 text-left text-[12px] leading-snug transition-colors ${
            isActive
              ? "text-fs-text-primary-inverse bg-[#7B9EFF]/10 font-medium"
              : "text-[#ddd] hover:bg-[#1f1f1f]"
          }`}
        >
          <span
            className={`mt-[5px] inline-block h-1.5 w-1.5 shrink-0 rounded-full ${
              isExact || (isActive && nested.length === 0)
                ? "bg-[#7B9EFF]"
                : isActive
                  ? "bg-[#7B9EFF]/60"
                  : "bg-[#555]"
            }`}
          />
          <span className="min-w-0">{v.label}</span>
        </button>
        {v.contentAfter}
        {nested.length > 0 ? (
          <div className="mt-0.5 ml-4">
            {nested.map((option) => renderNestedVariantButton(group, option))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderNestedVariantButton = (group: VariantGroup, v: VariantOption) => {
    const isActive = v.variantNumber === group.active;
    return (
      <div key={v.variantNumber}>
        <button
          onClick={() => {
            const groupKey = buildGroupQueryKey(group);
            group.onChange(v.variantNumber);
            updateVariants({ groupKey, value: v.variantNumber });
          }}
          className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-1.5 text-left text-[12px] leading-snug transition-colors ${
            isActive
              ? "text-fs-text-primary-inverse bg-[#7B9EFF]/10 font-medium"
              : "text-[#ddd] hover:bg-[#1f1f1f]"
          }`}
        >
          {/* Hollow, where a top-level option's dot is filled: the ring is what marks
            these as tuners on the option above rather than siblings of it. */}
          <span
            className={`mt-[4px] inline-block h-2 w-2 shrink-0 rounded-full border ${
              isActive ? "border-[#7B9EFF]" : "border-[#555]"
            }`}
          />
          <span className="min-w-0">{v.label}</span>
        </button>
        {isActive ? v.contentAfter : null}
      </div>
    );
  };

  const renderGroupControls = (group: VariantGroup, showTitle: boolean) => (
    <div key={group.queryKey || group.title || group.accordion}>
      {showTitle && group.title ? (
        <div className="mb-1.5 text-[10px] font-medium tracking-wide text-[#aaa] uppercase">
          {group.title}
        </div>
      ) : null}
      {group.toggleMode && group.variants.length === 2 ? (
        <div className="flex items-center gap-2 px-0.5 py-0.5">
          <button
            onClick={() => {
              const groupKey = buildGroupQueryKey(group);
              group.onChange(group.variants[0].variantNumber);
              updateVariants({
                groupKey,
                value: group.variants[0].variantNumber,
              });
            }}
            className={`text-[12px] transition-colors ${
              group.active === group.variants[0].variantNumber
                ? "text-fs-text-primary-inverse font-medium"
                : "text-[#666]"
            }`}
          >
            {group.variants[0].label}
          </button>
          <button
            onClick={() => {
              const next =
                group.active === group.variants[0].variantNumber
                  ? group.variants[1].variantNumber
                  : group.variants[0].variantNumber;
              const groupKey = buildGroupQueryKey(group);
              group.onChange(next);
              updateVariants({ groupKey, value: next });
            }}
            className="relative h-5 w-9 shrink-0 rounded-full transition-colors duration-200"
            style={{
              backgroundColor:
                group.active === group.variants[1].variantNumber
                  ? "#7B9EFF"
                  : "#444",
            }}
          >
            <span
              className="bg-fs-surface-primary absolute top-0.5 h-4 w-4 rounded-full shadow transition-all duration-200"
              style={{
                left:
                  group.active === group.variants[1].variantNumber
                    ? "18px"
                    : "2px",
              }}
            />
          </button>
          <button
            onClick={() => {
              const groupKey = buildGroupQueryKey(group);
              group.onChange(group.variants[1].variantNumber);
              updateVariants({
                groupKey,
                value: group.variants[1].variantNumber,
              });
            }}
            className={`text-[12px] transition-colors ${
              group.active === group.variants[1].variantNumber
                ? "text-fs-text-primary-inverse font-medium"
                : "text-[#666]"
            }`}
          >
            {group.variants[1].label}
          </button>
        </div>
      ) : (
        <div className="space-y-2 py-0.5">
          {buildVariantBlocks(group.variants).map((block) => {
            if (block.type === "plain") {
              return (
                <div key={block.variants.map((v) => v.variantNumber).join("-")}>
                  {block.variants.map((v) => renderVariantButton(group, v))}
                </div>
              );
            }

            const sectionContainsActive = block.variants.some(
              (v) => v.variantNumber === group.active
            );

            return (
              <AccordionSection
                key={block.label}
                label={block.label}
                defaultOpen={sectionContainsActive}
              >
                <div>
                  {block.variants.map((v) => renderVariantButton(group, v))}
                </div>
              </AccordionSection>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="w-64 rounded-xl border border-[#2a2a2a] bg-[#141414] shadow-[0_2px_8px_rgba(0,0,0,0.25)]">
      <div className="border-b border-[#2a2a2a] px-3 py-2">
        <span className="text-[11px] font-medium text-[#aaa]">Variants</span>
      </div>
      {variantGroups.length > 0 ? (
        <div className="space-y-4 p-3">
          {sections.map((section) => {
            if (section.type === "group") {
              return renderGroupControls(section.group, true);
            }

            return (
              <AccordionSection key={section.label} label={section.label}>
                {section.groups.map((group) =>
                  renderGroupControls(group, true)
                )}
              </AccordionSection>
            );
          })}
        </div>
      ) : (
        <div className="p-4">
          <p className="text-[12px] text-[#aaa]">No variants yet.</p>
        </div>
      )}
    </div>
  );
};
